import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { TransferStateDto } from '@mimir/common/dto/transferRequestDtos';
import { invariant } from '@mimir/common/util/invariant';
import { importSubclip, type SubclipDownload } from './importSubclip';
import { importToPremiereAsync } from './importToPremiere';
import { openProjectFile } from './premiereProject';
import { updateExistingClip } from './updateExistingClip';
import { type AdobePanelContext, type ElectronContext, isInPremiere } from '@mimir/common/context/applicationContext';
import { convertSecondsToTicks } from '@mimir/client-common/util/premiere';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import { getGlobalStore } from '../store';


function isSubclipDownload(od: OngoingDownload): od is SubclipDownload {
  return od.subclip !== undefined && od.subclip !== null && od.title != null;
}


export async function completeAndImport(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  ongoingDownload: OngoingDownload,
  // The path that has been verified as being locally available
  locallyAvailablePath: string,
) {
  // File available since last checked. Import and mark completion.
  ongoingDownload.transferState = TransferStateDto.COMPLETE;

  if (ongoingDownload.postDownloadHooks !== undefined) {
    await Promise.all(ongoingDownload.postDownloadHooks.map(h => h(locallyAvailablePath)));
  }

  if (ongoingDownload.importAfterCompletion) {
    if (ongoingDownload.downloadDestination?.type === 'builtin-project-file-download') {
      const title = ongoingDownload.title!;
      await openProjectFile(services, config, locallyAvailablePath, title!, ongoingDownload.itemId!, false, ongoingDownload.isProjectTemplate);
      return true;
    }


    const mimirId = ongoingDownload.isSubclipItem ? '' : ongoingDownload.itemId;
    if (ongoingDownload.subclip) {
      invariant(isSubclipDownload(ongoingDownload), 'Expected SubclipDownload');
      await importSubclip(services.applicationContext, ongoingDownload, locallyAvailablePath, config.shouldUsePremierePreferences);
    } else {
      await importToPremiereAsync(
        services.applicationContext,
        {
          filePath: locallyAvailablePath,
          title: ongoingDownload.title!,
          sourceItemId: mimirId,
          proxyFilePath: ongoingDownload.artefact === 'proxy' ? locallyAvailablePath : undefined,
          tapeName: ongoingDownload.originalFileName,
          artifactReference: ongoingDownload.artifactReference ?? null,
          insertIntoSequence: ongoingDownload.insertIntoSequence,
          markers: ongoingDownload.markers,
          startTimeInTicks: ongoingDownload.premiereStartTimeSeconds ? convertSecondsToTicks(ongoingDownload.premiereStartTimeSeconds) : null,
          timeDisplayFormat: ongoingDownload.premiereTimeDisplayFormat ?? null,
          mediaFrameRate: ongoingDownload.mediaFrameRate,
        },
        config.shouldUsePremierePreferences,
        ongoingDownload.targetBin,
      );
    }
  } else if (isInPremiere(services.applicationContext) || getGlobalStore().getters.dvrMode) {
    await updateExistingClip(services.applicationContext, ongoingDownload, locallyAvailablePath);
  }

  return true;
}
